import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deleteUserService,
  getUserByIdService,
  updatePatientMrnService,
  updatePatientStatsService,
} from "../api/services";
import { PlanDetails } from "../components/PlanDetails";
import { useToast } from "../components/Toast";
import { PlanStatusEnum } from "../constants/enums";
import { useRequestError } from "../hooks/useRequestError";
import type { UserModel, UserStatsModel } from "../types/models";
import {
  getLifeStyleStatus,
  getPrescriptionStatus,
  getVitalStatus,
} from "../utils/getVitalsStatus";

export const PatientDetailPage = () => {
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { handleRequestError } = useRequestError();
  const [user, setUser] = useState<UserModel | null>(null);
  const [editing, setEditing] = useState(false);
  const [mrnValue, setMrnValue] = useState("");
  const [formData, setFormData] = useState<
    Omit<UserStatsModel, "created_at" | "updated_at">
  >({
    sleep_hours: 0,
    steps_count: 0,
    prescription: [],
    blood_systolic_pressure: 0,
    blood_diastolic_pressure: 0,
    blood_glucose: 0,
    heart_rate: 0,
  });

  const load = async () => {
    try {
      const res = await getUserByIdService(userId);
      setUser(res);
      setMrnValue(res.external_mrn || "");
      const stats = res.vital_stats;
      const planPrescriptions = res.plans?.[0]?.data?.prescription || [];
      const prescriptionData = planPrescriptions.map((planPrescription) => {
        const existing = stats?.prescription?.find(
          (p) => p.name === planPrescription.name
        );
        return {
          name: planPrescription.name,
          taken: existing?.taken ?? false,
        };
      });
      setFormData({
        sleep_hours: Number(stats?.sleep_hours) || 0,
        steps_count: Number(stats?.steps_count) || 0,
        prescription: prescriptionData,
        blood_systolic_pressure: Number(stats?.blood_systolic_pressure) || 0,
        blood_diastolic_pressure: Number(stats?.blood_diastolic_pressure) || 0,
        blood_glucose: Number(stats?.blood_glucose) || 0,
        heart_rate: Number(stats?.heart_rate) || 0,
      });
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404 || status === 401) {
        showToast({
          type: "error",
          title: "User Not Found",
          message: "This patient account may have been deleted.",
        });
        navigate("/");
        return;
      }
      handleRequestError(error);
    }
  };

  useEffect(() => {
    if (userId) {
      load();
    }
  }, [userId]);

  const plan = user?.plans?.[0];
  const hasActivePlan = Boolean(
    plan?.id && plan.status === PlanStatusEnum.ACTIVE && plan.data
  );

  const saveStats = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await updatePatientStatsService(userId, formData as UserStatsModel);
      setEditing(false);
      await load();
      showToast({
        type: "success",
        title: "Success",
        message: "Patient stats updated successfully.",
      });
    } catch (error) {
      handleRequestError(error);
    }
  };

  const saveMrn = async () => {
    if (!mrnValue.trim()) {
      showToast({ type: "error", title: "Error", message: "Please enter a valid MRN" });
      return;
    }
    try {
      await updatePatientMrnService(userId, mrnValue.trim());
      await load();
      showToast({ type: "success", title: "Success", message: "MRN updated successfully" });
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        showToast({
          type: "error",
          title: "Error",
          message: "This MRN is already assigned to another patient",
        });
      } else {
        handleRequestError(error);
      }
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this patient account?")) return;
    try {
      await deleteUserService(userId);
      navigate("/");
    } catch (error) {
      handleRequestError(error);
    }
  };

  if (!user) {
    return (
      <div className="page">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>
            {user.first_name} {user.last_name}
          </h1>
          <p className="muted">{user.email}</p>
        </div>
        <div className="actions">
          {hasActivePlan ? (
            <Link className="btn btn-secondary" to={`/patients/${userId}/vitals`}>
              Vitals history
            </Link>
          ) : null}
          {plan?.id ? (
            <Link className="btn btn-secondary" to={`/plans/${plan.id}`}>
              View plan
            </Link>
          ) : (
            <Link
              className="btn btn-primary"
              to={`/plans/setup?user_id=${userId}`}
            >
              Create plan
            </Link>
          )}
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </header>

      {!hasActivePlan ? (
        <div className="empty-state">No Plan Yet..</div>
      ) : (
        <>
          <section className="panel stack gap-sm">
            <div className="row-between">
              <strong>Hospital MRN</strong>
              <button type="button" className="text-btn" onClick={saveMrn}>
                {mrnValue.trim() ? "Edit" : "Add"}
              </button>
            </div>
            <input
              value={mrnValue}
              onChange={(e) => setMrnValue(e.target.value.toUpperCase())}
              placeholder="Enter MRN (e.g., MRN123456)"
            />
            {!user.external_mrn ? (
              <p className="muted">
                Not set - tap Add to link hospital records
              </p>
            ) : null}
          </section>

          <section className="panel stack gap-md">
            <div className="status-chips">
              <span>
                Lifestyle:{" "}
                {getLifeStyleStatus(plan!.data.lifestyle, user.vital_stats) ||
                  "—"}
              </span>
              <span>
                Vitals:{" "}
                {getVitalStatus(plan!.data.vitals, user.vital_stats) || "—"}
              </span>
              <span>
                Rx:{" "}
                {getPrescriptionStatus(
                  plan!.data.prescription,
                  user.vital_stats
                ) || "—"}
              </span>
            </div>
            <PlanDetails
              description={plan!.description}
              startDate={plan!.start_date}
              endDate={plan!.end_date}
              lifestyle={plan!.data.lifestyle}
              vitals={plan!.data.vitals}
              prescription={plan!.data.prescription}
              currentStats={user.vital_stats}
            />
          </section>

          <section className="panel">
            <div className="row-between" style={{ marginBottom: 12 }}>
              <h3>Current stats</h3>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditing((v) => !v)}
              >
                {editing ? "Cancel" : "Edit stats"}
              </button>
            </div>
            {editing ? (
              <form className="form-grid" onSubmit={saveStats}>
                {(
                  [
                    ["sleep_hours", "Sleep hours"],
                    ["steps_count", "Steps"],
                    ["blood_systolic_pressure", "Systolic"],
                    ["blood_diastolic_pressure", "Diastolic"],
                    ["blood_glucose", "Glucose"],
                    ["heart_rate", "Heart rate"],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="field">
                    <span>{label}</span>
                    <input
                      type="number"
                      value={formData[key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [key]: Number(e.target.value),
                        }))
                      }
                    />
                  </label>
                ))}
                {formData.prescription.map((item, index) => (
                  <label key={`${item.name}-${index}`} className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={item.taken}
                      onChange={(e) => {
                        const next = [...formData.prescription];
                        next[index] = { ...item, taken: e.target.checked };
                        setFormData((prev) => ({ ...prev, prescription: next }));
                      }}
                    />
                    <span>{item.name} taken</span>
                  </label>
                ))}
                <button type="submit" className="btn btn-primary">
                  Save stats
                </button>
              </form>
            ) : (
              <div className="detail-grid">
                <div>Sleep: {user.vital_stats?.sleep_hours ?? "—"}</div>
                <div>Steps: {user.vital_stats?.steps_count ?? "—"}</div>
                <div>
                  Systolic: {user.vital_stats?.blood_systolic_pressure ?? "—"}
                </div>
                <div>
                  Diastolic: {user.vital_stats?.blood_diastolic_pressure ?? "—"}
                </div>
                <div>Glucose: {user.vital_stats?.blood_glucose ?? "—"}</div>
                <div>Heart rate: {user.vital_stats?.heart_rate ?? "—"}</div>
              </div>
            )}
          </section>

          <button type="button" className="btn btn-danger" onClick={onDelete}>
            Delete patient
          </button>
        </>
      )}
    </div>
  );
};
